import {parse} from 'csv-parse/browser/esm/sync';
import {DateTime} from 'luxon';
import React from 'react';
import {useQueryClient} from 'react-query';

import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import {
  ApiObservations,
  FLOW_TYPES,
  FlowType,
  UNIT_OPTIONS,
  Unit,
  uploadObservations,
} from 'src/lib/api/observations';
import * as tw from 'src/lib/tailwindClasses';
import FormSection from 'src/pages/Settings/FormSection';

const REQUIRED_COLUMNS = ['siteId', 'datetime'];
const OPTIONAL_COLUMNS = FLOW_TYPES;

type UploadState = {
  csv: string[][] | null;
  error: string | null;
  uploadStatus: {type: 'success' | 'error'; message: string} | null;
  isUploading: boolean;
};

const initialState: UploadState = {
  csv: null,
  error: null,
  uploadStatus: null,
  isUploading: false,
};

export default function Observations() {
  const {t} = useTranslations();
  const queryClient = useQueryClient();
  const currentOrg = useCurrentOrganization()!;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [units, setUnits] = React.useState<Unit>('cms');
  const [state, setState] = React.useState<UploadState>(initialState);

  const clearFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setState((prev) => ({...prev, csv: null, error: null}));
  };

  const validateCsv = (data: string[][]): void => {
    const [headers, ...rows] = data;

    // Validate headers
    const missingColumns = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
    if (missingColumns.length > 0) {
      setState((prev) => ({
        ...prev,
        error: t('missingRequiredColumns', {columns: missingColumns.join(', ')}),
      }));
      return;
    }

    if (!OPTIONAL_COLUMNS.some((col) => headers.includes(col))) {
      setState((prev) => ({
        ...prev,
        error: t('atLeastOneColumnRequired', {columns: OPTIONAL_COLUMNS.join(', ')}),
      }));
      return;
    }

    // Validate data
    const siteIds = rows.map((row) => row[headers.indexOf('siteId')]);
    if (new Set(siteIds).size !== 1) {
      setState((prev) => ({...prev, error: t('singleSiteIdRequired')}));
      return;
    }

    const datetimeColumnIndex = headers.indexOf('datetime');
    const invalidDatetimes = rows
      .map((row) => row[datetimeColumnIndex])
      .filter((datetime) => {
        // Validate that the datetimes are valid ISO strings that
        // include a timezone offset.
        const hasTimezone = /[+-]\d{2}:?\d{2}$/.test(datetime);
        const dt = DateTime.fromISO(datetime);

        return !dt.isValid || !hasTimezone;
      });

    if (invalidDatetimes.length > 0) {
      setState((prev) => ({
        ...prev,
        error: t('invalidDatetimeFormat'),
      }));
      return;
    }

    if (rows.length > 1000) {
      setState((prev) => ({...prev, error: t('tooManyRows')}));
      return;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Reset state
    setState(initialState);

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.endsWith('.csv')) {
      setState((prev) => ({...prev, error: t('fileMustBeCsv')}));
      return;
    }

    try {
      const text = await file.text();
      const records = parse(text, {
        skip_empty_lines: true,
        trim: true,
        columns: false,
        relax_column_count: true,
        relax_quotes: true,
      }) as string[][];

      if (records.length === 0) {
        setState((prev) => ({...prev, error: t('csvFileEmpty')}));
        return;
      }

      validateCsv(records);
      setState((prev) => ({...prev, csv: records}));
    } catch (err) {
      console.error('Failed to parse CSV file', err);
      setState((prev) => ({...prev, error: t('failedToParseCsv')}));
    }
  };

  const onSubmit = async () => {
    if (!state.csv) return;

    setState((prev) => ({...prev, isUploading: true}));

    try {
      const [observations, siteId] = transformCsvToObservations(state.csv, units);
      const response = await uploadObservations(observations, siteId);

      if (response.status === 200) {
        setState((prev) => ({
          ...prev,
          uploadStatus: {type: 'success', message: t('uploadSuccess')},
        }));
        // Obs and forecast series are cached under the 'queries' root with
        // staleTime Infinity, so charts only pick up the upload if marked stale.
        queryClient.invalidateQueries('queries');
        clearFile();
      } else {
        setState((prev) => ({
          ...prev,
          uploadStatus: {
            type: 'error',
            message: t('uploadError', {message: response.error || t('failedToParseCsv')}),
          },
        }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        uploadStatus: {
          type: 'error',
          message: t('uploadError', {
            message: err instanceof Error ? err.message : t('failedToParseCsv'),
          }),
        },
      }));
    } finally {
      setState((prev) => ({...prev, isUploading: false}));
    }
  };

  const transformCsvToObservations = (csv: string[][], units: Unit): [ApiObservations, string] => {
    const [headers, ...rows] = csv;

    // Get column indices for required and optional columns
    const siteIdIndex = headers.indexOf('siteId');
    const datetimeIndex = headers.indexOf('datetime');

    const siteId = rows[0][siteIdIndex]; // Get siteId from the correct column
    const flowTypesAndColumnNumber = FLOW_TYPES.map((col) => ({
      type: col as FlowType,
      columnNumber: headers.indexOf(col),
    })).filter(({columnNumber}) => columnNumber !== -1);

    const observations: ApiObservations = {
      units: flowTypesAndColumnNumber.reduce(
        (acc, {type}) => ({
          ...acc,
          [type as FlowType]: units,
        }),
        {} as Partial<{[key in FlowType]: string}>
      ),
      datetime: rows.map((row) => row[datetimeIndex]),
      ...flowTypesAndColumnNumber.reduce(
        (acc, {type, columnNumber}) => ({
          ...acc,
          [type]: rows.map((row) => row[columnNumber]),
        }),
        {}
      ),
    };

    return [observations, siteId];
  };

  const readyToSubmit = state.csv && units && !state.error;

  return (
    <PageContainer>
      <PageTitle
        pageStack={[
          {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
          {title: t('settings'), to: '/:currentOrgIdPrefix/settings'},
          {title: t('observations'), to: '/:currentOrgIdPrefix/settings/observations'},
        ]}
      />

      <FormSection
        name={t('uploadObservations')}
        description={
          <>
            {t('uploadObservationsInfo')}{' '}
            <a
              className="text-blue-600"
              href="https://hydroforecast.com/support/article/196-put-observations-api-route/"
              target="_blank"
              rel="noreferrer"
            >
              {t('documentation')}
            </a>
            .
          </>
        }
        isLoading={false}
        onSubmit={onSubmit}
        hasPermissions={true}
        customFooter={() => (
          <button
            type="submit"
            className={tw.buttonPrimary}
            disabled={!readyToSubmit || state.isUploading}
          >
            {t('upload')}
          </button>
        )}
        render={() => (
          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
                ref={fileInputRef}
              />
              <label
                htmlFor="csv-upload"
                className="border border-gray-300 rounded-md p-4 border-dashed flex flex-col items-center justify-center min-h-24 hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                {state.csv ? (
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium break-all">
                          {fileInputRef.current?.files?.[0]?.name}
                        </span>
                        <span className="text-sm text-gray-500">({state.csv.length - 1} rows)</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          clearFile();
                        }}
                        className={tw.button}
                      >
                        {t('remove')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('clickToSelectCsv')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('requiredColumns')}: {REQUIRED_COLUMNS.join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('oneOrMoreOf', {types: OPTIONAL_COLUMNS.join(', ')})}
                    </p>
                  </div>
                )}
              </label>
              {state.error && <p className="text-red-500 text-xs">{state.error}</p>}
            </div>

            {state.csv && (
              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 mb-2">{t('csvPreview')}</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {state.csv[0].map((header, index) => (
                          <th
                            key={index}
                            scope="col"
                            className="px-3 py-2 text-left text-xs font-medium max-w-6 text-gray-500 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {state.csv.slice(1, 6).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="px-3 py-2 whitespace-nowrap overflow-hidden text-ellipsis text-sm max-w-6 text-gray-500"
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {state.csv.length > 6 && (
                        <tr>
                          <td
                            colSpan={state.csv[0].length}
                            className="px-3 py-2 text-sm text-gray-500"
                          >
                            {t('andMoreRows', {count: String(state.csv.length - 6)})}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="units" className="block text-sm font-medium text-gray-700 mb-2">
                {t('units')}
              </label>
              <select
                id="units"
                className={tw.select}
                value={units}
                onChange={(event) => setUnits(event.target.value as Unit)}
              >
                {UNIT_OPTIONS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
            {state.uploadStatus && (
              <p
                className={`${state.uploadStatus.type === 'success' ? 'text-blue-500' : 'text-red-500'} text-xs`}
              >
                {state.uploadStatus.message}
              </p>
            )}
          </div>
        )}
      />
    </PageContainer>
  );
}
